// ============================================================================
// ACCOUNT CASES SERVICE (ACCT-CASES-001)
// ============================================================================

import { get, axiosClient } from '../client/axiosClient';
import { accountCasesEndpoints, ACCT_CASES_BASE_PATH } from '../endpoints/accountCases.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, ID, PaginatedResponse } from '../types';
import type {
  AccountCases,
  AccountImportResult,
  ActionableAccountRow,
  ActionableAccountSearchRequest,
} from '../types/accountCases.types';

const toPaginated = <T>(raw: any): PaginatedResponse<T> => {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 25,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
};

class AccountCasesService {
  async getByAccount(accountId: ID): Promise<ApiResponse<AccountCases>> {
    const response = await get<ApiResponse<AccountCases>>(
      accountCasesEndpoints.byAccount(accountId),
    );
    return response.data;
  }

  async getActionable(params?: ActionableAccountSearchRequest): Promise<PaginatedResponse<ActionableAccountRow>> {
    const endpoint = params
      ? buildEndpointWithQuery(`${ACCT_CASES_BASE_PATH}/actionable`, params as Record<string, any>)
      : accountCasesEndpoints.actionable();
    const res = await get<any>(endpoint);
    // Backend returns rows with `accountId` but no top-level `id`. Map for SyutGrid.
    const paged = toPaginated<ActionableAccountRow>(res.data);
    paged.data = paged.data.map(r => ({ ...r, id: (r as any).id ?? r.accountId }));
    return paged;
  }

  async downloadImportTemplate(): Promise<void> {
    const res = await axiosClient.get(accountCasesEndpoints.importTemplate(), {
      responseType: 'blob',
    });
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'recsuit_account_import_template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  async importFile(file: File, mode: 'strict' | 'lenient' = 'strict'): Promise<AccountImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const url = buildEndpointWithQuery(`${ACCT_CASES_BASE_PATH}/import`, { mode });
    const res = await axiosClient.post<ApiResponse<AccountImportResult>>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data as AccountImportResult;
  }
}

export const accountCasesService = new AccountCasesService();
export default accountCasesService;
